/** Bot memory state: the workspace summary (`enabled` false means render
 * nothing) and the notes of one scope. A failed summary load hides the
 * feature, as it does for schedules. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { botMemoryService, type BotMemoryScope, type BotMemorySummary, type MemoryItem } from '../../../services/BotMemoryService';
import {
  BOT_MEMORY_CHANGED_EVENT, announceMemoryChanged, memoryCacheKey, memoryVersion, readMemoryCache, writeMemoryCache,
  type BotMemoryChangedDetail,
} from './botMemoryStore';

export { clearBotMemoryCache } from './botMemoryStore';

interface Loaded { key: string; summary: BotMemorySummary | null }

export function useBotMemorySummary(workspaceId: string) {
  const key = memoryCacheKey(workspaceId);
  const [data, setData] = useState<Loaded | null>(() => {
    const hit = readMemoryCache(key);
    return hit ? { key, summary: hit } : null;
  });
  const [reloads, setReloads] = useState(0);
  const origin = useRef(crypto.randomUUID());

  useEffect(() => {
    const onChanged = (event: Event) => {
      const detail = (event as CustomEvent<BotMemoryChangedDetail>).detail;
      if (detail?.workspaceId === workspaceId && detail.origin !== origin.current) setReloads((n) => n + 1);
    };
    window.addEventListener(BOT_MEMORY_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(BOT_MEMORY_CHANGED_EVENT, onChanged);
  }, [workspaceId]);

  useEffect(() => {
    const hit = readMemoryCache(key);
    if (hit) { setData((previous) => (previous?.key === key && previous.summary === hit ? previous : { key, summary: hit })); return; }
    let active = true;
    const startedAt = memoryVersion(workspaceId);
    botMemoryService.summary(workspaceId)
      .then((result) => {
        if (!active || memoryVersion(workspaceId) !== startedAt) return;
        writeMemoryCache(key, workspaceId, result);
        setData({ key, summary: result });
      })
      .catch(() => { if (active) setData({ key, summary: null }); });
    return () => { active = false; };
  }, [workspaceId, key, reloads]);

  /** Announces a change to other views and refetches this one. */
  const refresh = useCallback(() => { announceMemoryChanged(workspaceId, origin.current); setReloads((n) => n + 1); }, [workspaceId]);
  const current = data?.key === key ? data : null;
  return { summary: current?.summary ?? null, loaded: current !== null, refresh };
}

/** Always fetched fresh when the view opens; nothing here is cached. */
export function useBotMemoryNotes(workspaceId: string, scope: BotMemoryScope, reloadKey = 0) {
  const [notes, setNotes] = useState<MemoryItem[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [clearPending, setClearPending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setNotes([]);
    setLoading(true);
    setFailed(false);
    botMemoryService.list(workspaceId, scope)
      .then((result) => {
        if (!active) return;
        setNotes(result.memories); setTruncated(result.truncated); setClearPending(result.clearPending);
      })
      .catch(() => { if (active) setFailed(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [workspaceId, scope, reloadKey]);

  const drop = useCallback((id: string) => setNotes((previous) => previous.filter((note) => note.id !== id)), []);
  const emptied = useCallback((pending: boolean) => { setNotes([]); setTruncated(false); setClearPending(pending); }, []);
  return { notes, truncated, clearPending, loading, failed, drop, emptied };
}
